import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, Card } from "react-bootstrap";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { SearchableTable } from "@fyrstain/hl7-front-library";
import { useTranslation } from "react-i18next";
import { Patient, Person, Practitioner } from "fhir/r5";
import JunoPage from "../../components/JunoPage/JunoPage";
import PatientService from "../../services/PatientService";
import PersonService from "../../services/PersonService";
import PractitionerService from "../../services/PractitionerService";
import {
  findLinkedPersonsForTarget,
  formatBirthDate,
  getFullName,
  getGivenNames,
  getPrimaryIdentifier,
  getSectionResourceType,
  getTelecomValue,
  IndividualSection,
  resolveLinkedIndividuals,
} from "../../utils/individuals";
import "./Individuals.css";

type SectionConfig<TResource extends Person | Patient | Practitioner> = {
  badgeVariant: string;
  columns: {
    dataField: string;
    header: string;
    width: string;
  }[];
  key: IndividualSection;
  mapResourceToData: (resource: TResource) => Record<string, string>;
  resourceType: TResource["resourceType"];
  searchInputs: {
    label: string;
    searchParamsName: string;
    type: "text";
  }[];
  searchParams: Record<string, string>;
  titleKey: string;
};

// Helpers
function normalizeSection(value?: string | null): IndividualSection {
  const normalizedValue = (value ?? "").trim().toLowerCase();

  if (normalizedValue === "patients") {
    return "patients";
  }

  if (normalizedValue === "practitioners") {
    return "practitioners";
  }

  return "users";
}

const Individuals: FunctionComponent = () => {
  //////////////////////////////
  //        Navigation        //
  //////////////////////////////

  const { i18n: translationI18n, t } = useTranslation();
  const navigate = useNavigate();
  const { section } = useParams();
  const activeSection = normalizeSection(section);

  //////////////////////////////
  //           Data           //
  //////////////////////////////

  const [loading, setLoading] = useState(false);
  const [persons, setPersons] = useState<Person[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);

  const onError = useCallback(() => {
    navigate("/Error");
  }, [navigate]);

  //////////////////////////////
  //      Data loading        //
  //////////////////////////////

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [peopleResponse, patientsResponse, practitionersResponse] =
        await Promise.all([
          PersonService.loadAllPersons(),
          PatientService.loadAllPatients(),
          PractitionerService.loadAllPractitioners(),
        ]);

      setPersons(peopleResponse);
      setPatients(patientsResponse);
      setPractitioners(practitionersResponse);
    } catch (error) {
      onError();
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  //////////////////////////////
  //      Configuration       //
  //////////////////////////////

  const sectionConfigs = useMemo(
    () =>
      [
        {
          key: "users",
          titleKey: "title.personlist",
          badgeVariant: "primary",
          resourceType: "Person",
          searchParams: {
            _count: "200",
            _elements: "id,name,identifier,active,gender,birthDate,link",
            _sort: "name",
          },
          searchInputs: [
            {
              label: "ID",
              type: "text",
              searchParamsName: "_id",
            },
            {
              label: t("label.identifier"),
              type: "text",
              searchParamsName: "identifier",
            },
            {
              label: t("label.lastname"),
              type: "text",
              searchParamsName: "family:contains",
            },
            {
              label: t("label.firstname"),
              type: "text",
              searchParamsName: "given:contains",
            },
          ],
          columns: [
            {
              header: t("label.identifier"),
              dataField: "identifier",
              width: "14%",
            },
            {
              header: t("label.fullname"),
              dataField: "name",
              width: "18%",
            },
            {
              header: t("label.resourceid"),
              dataField: "id",
              width: "16%",
            },
            {
              header: t("label.firstname"),
              dataField: "secondary",
              width: "14%",
            },
            {
              header: t("label.linkedresources"),
              dataField: "extra",
              width: "16%",
            },
            {
              header: t("label.birthdate"),
              dataField: "birthDate",
              width: "12%",
            },
          ],
          mapResourceToData: (resource: Person) => ({
            id: resource.id ?? "N/A",
            name: getFullName(resource),
            identifier: getPrimaryIdentifier(resource),
            secondary: getGivenNames(resource),
            extra:
              resolveLinkedIndividuals(resource, [], [])
                .map((linked) => linked.resourceType)
                .join(", ") || "N/A",
            birthDate: formatBirthDate(resource.birthDate),
          }),
        } satisfies SectionConfig<Person>,
        {
          key: "patients",
          titleKey: "title.patientlist",
          badgeVariant: "success",
          resourceType: "Patient",
          searchParams: {
            _count: "200",
            _elements: "id,name,identifier,active,gender,birthDate,telecom,address",
            _sort: "name",
          },
          searchInputs: [
            {
              label: "ID",
              type: "text",
              searchParamsName: "_id",
            },
            {
              label: t("label.identifier"),
              type: "text",
              searchParamsName: "identifier",
            },
            {
              label: t("label.lastname"),
              type: "text",
              searchParamsName: "family:contains",
            },
            {
              label: t("label.firstname"),
              type: "text",
              searchParamsName: "given:contains",
            },
            {
              label: t("label.email"),
              type: "text",
              searchParamsName: "email",
            },
          ],
          columns: [
            {
              header: t("label.identifier"),
              dataField: "identifier",
              width: "14%",
            },
            {
              header: t("label.fullname"),
              dataField: "name",
              width: "15%",
            },
            {
              header: t("label.resourceid"),
              dataField: "id",
              width: "16%",
            },
            {
              header: t("label.email"),
              dataField: "secondary",
              width: "20%",
            },
            {
              header: t("label.city"),
              dataField: "extra",
              width: "12%",
            },
            {
              header: t("label.birthdate"),
              dataField: "birthDate",
              width: "15%",
            },
          ],
          mapResourceToData: (resource: Patient) => ({
            id: resource.id ?? "N/A",
            name: getFullName(resource),
            identifier: getPrimaryIdentifier(resource),
            secondary: getTelecomValue(resource, "email"),
            extra:
              resource.address?.[0]?.city?.trim() ||
              resource.gender?.trim() ||
              "N/A",
            birthDate: formatBirthDate(resource.birthDate),
          }),
        } satisfies SectionConfig<Patient>,
        {
          key: "practitioners",
          titleKey: "title.practitionerlist",
          badgeVariant: "info",
          resourceType: "Practitioner",
          searchParams: {
            _count: "200",
            _elements:
              "id,name,identifier,active,gender,birthDate,telecom,qualification",
            _sort: "name",
          },
          searchInputs: [
            {
              label: "ID",
              type: "text",
              searchParamsName: "_id",
            },
            {
              label: t("label.identifier"),
              type: "text",
              searchParamsName: "identifier",
            },
            {
              label: t("label.lastname"),
              type: "text",
              searchParamsName: "family:contains",
            },
            {
              label: t("label.firstname"),
              type: "text",
              searchParamsName: "given:contains",
            },
            {
              label: t("label.email"),
              type: "text",
              searchParamsName: "email",
            },
          ],
          columns: [
            {
              header: t("label.identifier"),
              dataField: "identifier",
              width: "14%",
            },
            {
              header: t("label.fullname"),
              dataField: "name",
              width: "18%",
            },
            {
              header: t("label.resourceid"),
              dataField: "id",
              width: "16%",
            },
            {
              header: t("label.email"),
              dataField: "secondary",
              width: "20%",
            },
            {
              header: t("label.qualification"),
              dataField: "extra",
              width: "14%",
            },
            {
              header: t("label.birthdate"),
              dataField: "birthDate",
              width: "12%",
            },
          ],
          mapResourceToData: (resource: Practitioner) => ({
            id: resource.id ?? "N/A",
            name: getFullName(resource),
            identifier: getPrimaryIdentifier(resource),
            secondary: getTelecomValue(resource, "email"),
            extra:
              resource.qualification?.[0]?.code?.text?.trim() ||
              resource.qualification?.[0]?.code?.coding?.[0]?.display?.trim() ||
              resource.gender?.trim() ||
              "N/A",
            birthDate: formatBirthDate(resource.birthDate),
          }),
        } satisfies SectionConfig<Practitioner>,
      ] as const,
    [t]
  );

  const sectionConfig = useMemo(
    () =>
      sectionConfigs.find((config) => config.key === activeSection) ??
      sectionConfigs[0],
    [activeSection, sectionConfigs]
  );

  const summaryCards = useMemo(
    () => [
      {
        key: "users",
        label: t("label.users"),
        count: persons.length,
      },
      {
        key: "patients",
        label: t("label.patients"),
        count: patients.length,
      },
      {
        key: "practitioners",
        label: t("label.practitioners"),
        count: practitioners.length,
      },
    ],
    [patients.length, persons.length, practitioners.length, t]
  );

  const relationshipSummary = useMemo(
    () => ({
      personLinks: persons.reduce(
        (total, person) => total + (person.link?.length ?? 0),
        0
      ),
      patientLinks: patients.reduce(
        (total, patient) =>
          total +
          findLinkedPersonsForTarget(persons, "Patient", patient.id).length,
        0
      ),
      practitionerLinks: practitioners.reduce(
        (total, practitioner) =>
          total +
          findLinkedPersonsForTarget(
            persons,
            "Practitioner",
            practitioner.id
          ).length,
        0
      ),
    }),
    [patients, persons, practitioners]
  );

  //////////////////////////////
  //          Actions         //
  //////////////////////////////

  const onDetails = useCallback(
    (id: string) => {
      navigate(
        `/Individuals/${getSectionResourceType(activeSection)}/${encodeURIComponent(
          id
        )}`
      );
    },
    [activeSection, navigate]
  );

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <JunoPage
      titleKey={sectionConfig.titleKey}
      loading={loading}
      needsLogin={true}
    >
      <div className="section individuals-page">
        <Card className="card">
          <Card.Body className="individuals-layout">
            <div className="individuals-summary-grid">
              {summaryCards.map((card) => (
                <button
                  className={`individuals-summary-card ${
                    activeSection === card.key ? "is-active" : ""
                  }`}
                  key={card.key}
                  onClick={() => navigate(`/Individuals/${card.key}`)}
                  type="button"
                >
                  <span className="individuals-summary-label">{card.label}</span>
                  <span className="individuals-summary-value">{card.count}</span>
                </button>
              ))}
            </div>

            <div className="individuals-relationship-strip">
              <Badge bg="secondary">
                {t("label.personlinks")}: {relationshipSummary.personLinks}
              </Badge>
              <Badge bg="secondary">
                {t("label.patientlinks")}: {relationshipSummary.patientLinks}
              </Badge>
              <Badge bg="secondary">
                {t("label.practitionerlinks")}:{" "}
                {relationshipSummary.practitionerLinks}
              </Badge>
            </div>

            <div className="individuals-searchable-table">
              <SearchableTable
                key={`${activeSection}-${translationI18n.resolvedLanguage ?? translationI18n.language}`}
                searchCriteriaProperties={{
                  title: t("title.searchcriteria"),
                  submitButtonLabel: t("button.search"),
                  resetButtonLabel: t("button.reset"),
                  language: t,
                  fixedParameters: sectionConfig.searchParams,
                  inputs: sectionConfig.searchInputs,
                }}
                paginatedTableProperties={{
                  columns: sectionConfig.columns,
                  action: [
                    {
                      icon: faEye,
                      onClick: onDetails,
                    },
                  ],
                  mapResourceToData: sectionConfig.mapResourceToData,
                  searchProperties: {
                    serverUrl: process.env.REACT_APP_FHIR_URL ?? "fhir",
                    resourceType: sectionConfig.resourceType,
                  },
                  onError,
                }}
              />
            </div>
          </Card.Body>
        </Card>
      </div>
    </JunoPage>
  );
};

export default Individuals;
